import type { Immunization } from '@medplum/fhirtypes';

import { Grid, Stack, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import type { FhirView } from '../../core/view-contract';
import { resourceDisplayName } from '../accessors';
import { formatFhirDate } from '../observation/format';

/** The exported Immunization component. Immunization lists are the primary surface. */
export const ImmunizationView: FhirView<Immunization> = (props) => {
  if (props.ctx.inList) return <ImmunizationRowView {...props} />;
  return <ImmunizationReadView {...props} />;
};

function occurrenceText(data: Immunization): string | undefined {
  return formatFhirDate(data.occurrenceDateTime ?? data.recorded);
}

/** Immunization-list row: vaccine, status, date. */
const ImmunizationRowView: FhirView<Immunization> = ({ data }) => {
  const date = occurrenceText(data);
  return (
    <Grid
      data-view="immunization-row"
      align="center"
      className="grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-2"
    >
      <Stack direction="horizontal" gap={2} align="center" className="min-w-0">
        <Text inline size="sm" weight="regular" className="truncate">
          {resourceDisplayName(data)}
        </Text>
        {data.status !== 'completed' && <FhirStatusTag status={data.status} />}
      </Stack>
      {date && (
        <Text inline size="xs" color="tertiary">
          {date}
        </Text>
      )}
    </Grid>
  );
};

/** Standalone card. */
const ImmunizationReadView: FhirView<Immunization> = ({ data, ctx }) => {
  const date = occurrenceText(data);
  const lot = data.lotNumber;
  return (
    <div
      data-view="immunization-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold" className="truncate">
          {resourceDisplayName(data)}
        </Text>
        <FhirStatusTag status={data.status} />
      </Stack>
      <Stack
        direction="horizontal"
        wrap
        align="stretch"
        className="mt-1 gap-x-4"
      >
        {date && (
          <Text inline size="xs" color="tertiary">
            {date}
          </Text>
        )}
        {lot && (
          <Text inline size="xs" color="tertiary">
            Lot {lot}
          </Text>
        )}
      </Stack>
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
